// src/lib/aircraftClass.ts — 4-bucket aircraft performance classification.
//
// Used by RouteAdvisor to filter FlightAware routes to only those filed
// by aircraft in a similar performance class. A C172 pilot shouldn't
// see jet-only J-route filings; a Citation X pilot shouldn't see
// piston V-route filings.
//
// Spec: piston-single | piston-twin | turboprop | jet | unknown
//
// Adjacency (classesMatch):
//   piston-single ↔ piston-single, piston-twin
//   piston-twin   ↔ piston-single, piston-twin, turboprop
//   turboprop     ↔ piston-twin, turboprop, jet
//   jet           ↔ turboprop, jet
//   unknown       ↔ everything (permissive when we can't classify)

export type AircraftClass = 'piston-single' | 'piston-twin' | 'turboprop' | 'jet' | 'unknown';

// ---------------------------------------------------------------------------
// Lookup tables
// Entries are uppercase ICAO type designators (or prefix patterns with '*').
// Patterns are checked BEFORE exact matches, so be careful with ordering.
// ---------------------------------------------------------------------------

/** Prefix patterns that unambiguously belong to a class.
 *  Checked before the exact map using startsWith. */
const PREFIX_MAP: Array<[string, AircraftClass]> = [
  // CRJ series — all jets
  ['CRJ', 'jet'],
  // Citation 5xx/7xx/M2/XLS variants — all jets
  ['C5', 'jet'],
  ['C7', 'jet'],
  // B737 / B747 / B757 / B767 / B777 / B787 — all jets
  ['B73', 'jet'],
  ['B74', 'jet'],
  ['B75', 'jet'],
  ['B76', 'jet'],
  ['B77', 'jet'],
  ['B78', 'jet'],
  // Airbus A3xx — all jets
  ['A31', 'jet'],
  ['A31', 'jet'],
  ['A32', 'jet'],
  ['A33', 'jet'],
  ['A34', 'jet'],
  ['A35', 'jet'],
  ['A38', 'jet'],
  // TBM 700–960 series — turboprops
  ['TBM', 'turboprop'],
  // E-Jets
  ['E17', 'jet'],
  ['E19', 'jet'],
];

/** Exact ICAO type code → class.
 *  UPPERCASE only. Keys that appear in PREFIX_MAP don't need to be here
 *  but duplicates are harmless. */
const EXACT_MAP: Record<string, AircraftClass> = {
  // ── Piston singles ──────────────────────────────────────────────────────
  C172: 'piston-single',
  C152: 'piston-single',
  C162: 'piston-single',
  C182: 'piston-single',
  C150: 'piston-single',
  C170: 'piston-single',
  C177: 'piston-single',
  C210: 'piston-single',
  PA28: 'piston-single',
  PA24: 'piston-single',
  PA32: 'piston-single',
  SR20: 'piston-single',
  SR22: 'piston-single',
  DA40: 'piston-single',
  DA20: 'piston-single',
  RV6:  'piston-single',
  RV7:  'piston-single',
  RV8:  'piston-single',
  RV10: 'piston-single',
  GLST: 'piston-single', // Glasair
  M20P: 'piston-single', // Mooney
  M20J: 'piston-single',
  M20K: 'piston-single',
  M20T: 'piston-single',
  M20U: 'piston-single',
  F33A: 'piston-single', // Bonanza
  A36:  'piston-single',
  BE35: 'piston-single', // Bonanza V35
  BE33: 'piston-single', // Bonanza
  BE23: 'piston-single', // Musketeer

  // ── Piston twins ────────────────────────────────────────────────────────
  BE58: 'piston-twin',
  BE76: 'piston-twin', // Duchess
  BE55: 'piston-twin', // Baron
  PA34: 'piston-twin',
  C310: 'piston-twin',
  C340: 'piston-twin',
  C414: 'piston-twin',
  C421: 'piston-twin',
  PA31: 'piston-twin', // Navajo
  PA44: 'piston-twin', // Seminole
  BE60: 'piston-twin', // Duke
  P337: 'piston-twin', // Skymaster (pressurized)
  C336: 'piston-twin',
  C337: 'piston-twin',

  // ── Turboprops ──────────────────────────────────────────────────────────
  PC12: 'turboprop',
  C208: 'turboprop', // Caravan
  C20T: 'turboprop', // Caravan variant
  B190: 'turboprop',
  BE90: 'turboprop', // King Air C90
  BE9L: 'turboprop', // King Air C90GT
  BE99: 'turboprop',
  BE20: 'turboprop',
  BE30: 'turboprop',
  DHC6: 'turboprop',
  DHC8: 'turboprop',
  ATR4: 'turboprop',
  ATR7: 'turboprop',
  SF34: 'turboprop',
  // E55P → jet (Embraer Phenom 300), NOT turboprop — entry is below
  PAY3: 'turboprop', // Piper Cheyenne III
  PA42: 'turboprop', // Piper Cheyenne
  MU2:  'turboprop',
  // NOTE: PC24 is a JET (Pilatus PC-24), NOT a turboprop.

  // ── Jets ────────────────────────────────────────────────────────────────
  // Citation family
  C25A: 'jet', // Citation CJ2
  C25B: 'jet', // Citation CJ3
  C25C: 'jet', // Citation CJ4
  C500: 'jet', // Citation I
  C501: 'jet', // Citation I/SP
  C525: 'jet', // CitationJet/CJ1
  C550: 'jet', // Citation II
  C551: 'jet', // Citation II/SP
  C560: 'jet', // Citation V / Encore
  C56X: 'jet', // Citation Excel / XLS
  C68A: 'jet', // Citation Latitude
  C750: 'jet', // Citation X
  // Learjet
  LJ23: 'jet',
  LJ24: 'jet',
  LJ25: 'jet',
  LJ28: 'jet',
  LJ31: 'jet',
  LJ35: 'jet',
  LJ40: 'jet',
  LJ45: 'jet',
  LJ60: 'jet',
  LJ70: 'jet',
  LJ75: 'jet',
  // Bombardier Challenger / Global
  CL30: 'jet',
  CL35: 'jet',
  CL60: 'jet',
  GL5T: 'jet', // Global 5000
  GL7T: 'jet', // Global 7500
  GLEX: 'jet', // Global Express
  // Gulfstream
  G150: 'jet',
  G280: 'jet',
  G350: 'jet',
  G450: 'jet',
  G500: 'jet',
  G550: 'jet',
  G600: 'jet',
  G650: 'jet',
  GALX: 'jet', // Galaxy/G200
  // Dassault Falcon
  F2TH: 'jet',
  F900: 'jet',
  F7X:  'jet',
  F8X:  'jet',
  // Embraer Phenom / Praetor / Legacy
  E50P: 'jet', // Phenom 100
  E55P: 'jet', // Phenom 300 — overrides turboprop above (actually a jet)
  E545: 'jet', // Legacy 450
  E550: 'jet', // Legacy 500
  E135: 'jet', // ERJ-135
  E145: 'jet', // ERJ-145
  // Pilatus
  PC24: 'jet', // Pilatus PC-24 (NOT a turboprop)
  // HondaJet
  HDJT: 'jet',
  // Eclipse
  EA50: 'jet',
  // Airliner types (when encountered in GA context)
  A319: 'jet',
  A320: 'jet',
  A321: 'jet',
  B737: 'jet',
  B738: 'jet',
  B739: 'jet',
  B752: 'jet',
  B753: 'jet',
  B763: 'jet',
  B772: 'jet',
  B773: 'jet',
  B77L: 'jet',
  B77W: 'jet',
  B788: 'jet',
  B789: 'jet',
  B78X: 'jet',
};

// E55P = Embraer Phenom 300 (jet). King Air 350 uses BE20/BE30/BE9L.

// ---------------------------------------------------------------------------
// Classification API
// ---------------------------------------------------------------------------

/** Classify an ICAO aircraft type designator into one of the 4 performance
 *  buckets. Case-insensitive. Returns 'unknown' for unrecognised types
 *  (which is treated as permissive — matches everything). */
export function classifyAircraftType(icaoType: string | null | undefined): AircraftClass {
  if (!icaoType) return 'unknown';
  const t = icaoType.trim().toUpperCase();
  if (!t) return 'unknown';

  // Exact match wins.
  if (t in EXACT_MAP) return EXACT_MAP[t];

  // Prefix match (first matching prefix wins).
  for (const [prefix, cls] of PREFIX_MAP) {
    if (t.startsWith(prefix)) return cls;
  }

  return 'unknown';
}

// ---------------------------------------------------------------------------
// Compatibility matrix
// ---------------------------------------------------------------------------

const COMPATIBLE: Record<AircraftClass, Set<AircraftClass>> = {
  'piston-single': new Set(['piston-single', 'piston-twin']),
  'piston-twin':   new Set(['piston-single', 'piston-twin', 'turboprop']),
  'turboprop':     new Set(['piston-twin', 'turboprop', 'jet']),
  'jet':           new Set(['turboprop', 'jet']),
  'unknown':       new Set(['piston-single', 'piston-twin', 'turboprop', 'jet', 'unknown']),
};

/** Returns true when aircraft class `a` and class `b` are "compatible"
 *  for route filtering purposes. Symmetric. 'unknown' matches everything. */
export function classesMatch(a: AircraftClass, b: AircraftClass): boolean {
  if (a === 'unknown' || b === 'unknown') return true;
  return COMPATIBLE[a].has(b);
}

// ---------------------------------------------------------------------------
// Array helper used by RouteAdvisor
// ---------------------------------------------------------------------------

/** Returns true when any entry in `aircraftTypes` is compatible with
 *  `userClass`. Empty array → keep (no type info means permissive).
 *  'unknown' userClass → keep all. */
export function routeMatchesClass(
  aircraftTypes: string[],
  userClass: AircraftClass,
): boolean {
  if (userClass === 'unknown') return true;
  if (!aircraftTypes || aircraftTypes.length === 0) return true; // no info → permissive
  return aircraftTypes.some((t) => {
    const cls = classifyAircraftType(t);
    return classesMatch(userClass, cls);
  });
}

// ---------------------------------------------------------------------------
// Human-readable label
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// FAA approach-speed category classifier
// ---------------------------------------------------------------------------
//
// FAA approach categories (AIM 5-4-7) are based on Vref or 1.3 × Vso at
// maximum certified landing weight:
//   CAT A:  ≤90 kt
//   CAT B:  91–120 kt
//   CAT C: 121–140 kt
//   CAT D: 141–165 kt
//   CAT E:  ≥166 kt
//
// When an explicit approach speed (KIAS) is not on the aircraft profile we
// fall back to a per-class default based on typical values for that class:
//   piston-single → CAT A  (C172/C182 approach ~60-70 kt)  ← boss's 182
//   piston-twin   → CAT B  (PA34/BE58 approach ~80-100 kt)
//   turboprop     → CAT B  (PC12/King Air approach ~90-110 kt)
//   jet           → CAT C  (Citation/Gulfstream approach ~120-130 kt)
//   unknown       → CAT A  (permissive: tightest circling requirement)

export type ApproachCategory = 'A' | 'B' | 'C' | 'D' | 'E';

/** FAA approach-speed breakpoints (upper bound of each category, kt IAS). */
const APPROACH_CAT_BREAKPOINTS: Array<[number, ApproachCategory]> = [
  [ 90, 'A'],
  [120, 'B'],
  [140, 'C'],
  [165, 'D'],
];

/**
 * Classify an aircraft by FAA approach-speed category.
 *
 * @param approachSpeedKtIas  Explicit approach speed in KIAS (Vref or Vapp).
 *   When null, falls back to class-based default.
 * @returns Approach category 'A'–'E', or null when genuinely unknown.
 */
export function classifyAircraftApproachCategory(
  approachSpeedKtIas: number | null | undefined,
): ApproachCategory | null {
  if (approachSpeedKtIas == null) return null;
  const v = approachSpeedKtIas;
  for (const [upper, cat] of APPROACH_CAT_BREAKPOINTS) {
    if (v <= upper) return cat;
  }
  return 'E';
}

/** Per-class fallback approach category when no explicit speed is on the profile. */
const CLASS_DEFAULT_CAT: Record<AircraftClass, ApproachCategory> = {
  'piston-single': 'A',
  'piston-twin':   'B',
  'turboprop':     'B',
  'jet':           'C',
  'unknown':       'A',  // conservative: tightest radius
};

/**
 * Resolve the effective FAA approach category for a given aircraft profile.
 *
 * Priority:
 *   1. Explicit approach speed on the profile (vS0 × 1.3 or explicit vRef)
 *      → classifyAircraftApproachCategory(speed)
 *   2. ICAO type code on the profile → classify type → CLASS_DEFAULT_CAT
 *   3. Performance class default → CLASS_DEFAULT_CAT
 *
 * @param icaoType     ICAO type designator (optional, e.g. 'C182').
 * @param approachSpeedKtIas  Explicit approach speed KIAS (optional).
 */
export function getEffectiveApproachCategory(
  icaoType: string | null | undefined,
  approachSpeedKtIas?: number | null,
): ApproachCategory {
  // 1. Explicit speed wins
  if (approachSpeedKtIas != null) {
    return classifyAircraftApproachCategory(approachSpeedKtIas) ?? 'A';
  }
  // 2. ICAO type → class → default
  const cls = classifyAircraftType(icaoType);
  return CLASS_DEFAULT_CAT[cls];
}

export function aircraftClassLabel(cls: AircraftClass): string {
  switch (cls) {
    case 'piston-single': return 'piston single';
    case 'piston-twin':   return 'piston twin';
    case 'turboprop':     return 'turboprop';
    case 'jet':           return 'jet';
    default:              return '';
  }
}

// ---------------------------------------------------------------------------
// Inference from make/model string (fallback when icaoType is absent)
// ---------------------------------------------------------------------------

/** Try to infer an ICAO type code from a free-text make/model label.
 *  Returns null when no confident match found — callers should fall back
 *  to classifyAircraftType(null) → 'unknown' → permissive. */
export function inferIcaoFromTypeLabel(label: string | null | undefined): string | null {
  if (!label) return null;
  const s = label.trim().toUpperCase();

  const patterns: Array<[RegExp, string]> = [
    [/CESSNA\s*172|C172/, 'C172'],
    [/CESSNA\s*152|C152/, 'C152'],
    [/CESSNA\s*162|C162/, 'C162'],
    [/CESSNA\s*182|C182/, 'C182'],
    [/CESSNA\s*210|C210/, 'C210'],
    [/CESSNA\s*310|C310/, 'C310'],
    [/CESSNA\s*340|C340/, 'C340'],
    [/CESSNA\s*414|C414/, 'C414'],
    [/CESSNA\s*421|C421/, 'C421'],
    [/CESSNA\s*208|CARAVAN/, 'C208'],
    [/CITATION\s*CJ1|C525/, 'C525'],
    [/CITATION\s*CJ2|C25A/, 'C25A'],
    [/CITATION\s*CJ3|C25B/, 'C25B'],
    [/CITATION\s*CJ4|C25C/, 'C25C'],
    [/CITATION\s*EXCEL|XLS|C56X/, 'C56X'],
    [/CITATION\s*LATITUDE|C68A/, 'C68A'],
    [/CITATION\s*X\b|C750/, 'C750'],
    [/PIPER\s*CHEROKEE|PA[-\s]?28/, 'PA28'],
    [/PIPER\s*NAVAJO|PA[-\s]?31/, 'PA31'],
    [/PIPER\s*SENECA|PA[-\s]?34/, 'PA34'],
    [/PIPER\s*SEMINOLE|PA[-\s]?44/, 'PA44'],
    [/CIRRUS\s*SR[-\s]?20/, 'SR20'],
    [/CIRRUS\s*SR[-\s]?22/, 'SR22'],
    [/DIAMOND\s*DA[-\s]?40/, 'DA40'],
    [/DIAMOND\s*DA[-\s]?20/, 'DA20'],
    [/BEECH\s*BARON|BE[-\s]?58/, 'BE58'],
    [/BEECH\s*BONANZA|F33A/, 'F33A'],
    [/KING\s*AIR\s*350|BE[-\s]?20/, 'BE20'],
    [/KING\s*AIR\s*200|BE[-\s]?20/, 'BE20'],
    [/KING\s*AIR\s*90|BE[-\s]?9/, 'BE90'],
    [/PILATUS\s*PC[-\s]?12/, 'PC12'],
    [/PILATUS\s*PC[-\s]?24/, 'PC24'],
    [/PHENOM\s*100/, 'E50P'],
    [/PHENOM\s*300/, 'E55P'],
    [/GULFSTREAM\s*G650/, 'G650'],
    [/GULFSTREAM\s*G550/, 'G550'],
    [/GULFSTREAM\s*G450/, 'G450'],
    [/GULFSTREAM\s*G280/, 'G280'],
    [/LEARJET\s*45/, 'LJ45'],
    [/LEARJET\s*60/, 'LJ60'],
    [/LEARJET\s*75/, 'LJ75'],
  ];

  for (const [re, code] of patterns) {
    if (re.test(s)) return code;
  }
  return null;
}
